/**
 * Public DSCR Rate Center — shared, client-safe types and scenario helpers.
 *
 * Nothing in this module talks to a provider or touches credentials. It is
 * imported by both the public page and the server function so the scenario
 * shape stays in sync.
 */

import type { Freshness } from "./rate-freshness";
import type { StateAvailability } from "./rate-availability";
import type { LenderFeeEstimate } from "./lender-fee-rules";
import { resolvePrimaryLenderFee, type PrimaryLenderFee } from "./primary-lender-fee";
import type { PublicAprAudit } from "./apr-validation";
import type { AprBasisCode, AprExclusion, AprFinanceCharge, AprSource } from "./apr-engine";



export type PublicLoanPurpose = "purchase" | "rate_term_refinance" | "cash_out_refinance";

export type PublicRateScenario = {
  loanPurpose: PublicLoanPurpose;
  state: string;
  city: string;
  county: string;
  zipCode: string;
  propertyValue: number;
  loanAmount: number;
  fico: number;
  dscr: number;
  propertyType: "SingleFamily" | "Condo" | "Townhouse" | "PUD";
  units: 1 | 2 | 3 | 4;
  loanTerm: "ThirtyYear" | "FortyYear" | "FifteenYear";
  amortizationType: "Fixed" | "ARM";
  interestOnly: boolean;
  lockPeriodDays: number;
  prepaymentPenalty: "None" | "OneYear" | "TwoYear" | "ThreeYear" | "FourYear" | "FiveYear";
  shortTermRental: boolean;
  entityVesting: boolean;
  /**
   * Borrower citizenship. Materially changes DSCR/Non-QM eligibility and
   * pricing, so it is a first-class scenario input rather than an assumption.
   * Values are the exact enum the pricing engine accepts.
   */
  citizenship: PublicCitizenship;
  /**
   * Whether the borrower qualifies with an ITIN. Distinct from citizenship —
   * an ITIN borrower is not necessarily a foreign national, and vice versa.
   */
  hasITIN: boolean;
};

export type PublicCitizenship =
  | "USCitizen"
  | "PermResidentAlien"
  | "NonPermResidentAlien"
  | "ForeignNational";

export const CITIZENSHIP_LABEL: Record<PublicCitizenship, string> = {
  USCitizen: "U.S. citizen",
  PermResidentAlien: "Permanent resident",
  NonPermResidentAlien: "Non-permanent resident",
  ForeignNational: "Foreign national",
};


/** The controlled scenario the page server-renders by default. */
export const DEFAULT_RATE_SCENARIO: PublicRateScenario = {
  loanPurpose: "purchase",
  state: "FL",
  city: "Fort Lauderdale",
  county: "Broward",
  zipCode: "33301",
  propertyValue: 500_000,
  loanAmount: 375_000,
  fico: 740,
  dscr: 1.2,
  propertyType: "SingleFamily",
  units: 1,
  loanTerm: "ThirtyYear",
  amortizationType: "Fixed",
  interestOnly: false,
  lockPeriodDays: 30,
  prepaymentPenalty: "None",
  shortTermRental: false,
  entityVesting: true,
  citizenship: "USCitizen",
  hasITIN: false,
};

export const LOAN_PURPOSE_LABEL: Record<PublicLoanPurpose, string> = {
  purchase: "Purchase",
  rate_term_refinance: "Rate & term refinance",
  cash_out_refinance: "Cash-out refinance",
};

export const LOAN_TERM_LABEL: Record<PublicRateScenario["loanTerm"], string> = {
  FifteenYear: "15-year",
  ThirtyYear: "30-year",
  FortyYear: "40-year",
};

export const PREPAY_LABEL: Record<PublicRateScenario["prepaymentPenalty"], string> = {
  None: "No prepayment penalty",
  OneYear: "1-year prepay",
  TwoYear: "2-year prepay",
  ThreeYear: "3-year prepay",
  FourYear: "4-year prepay",
  FiveYear: "5-year prepay",
};

export function scenarioLtv(s: Pick<PublicRateScenario, "propertyValue" | "loanAmount">): number {
  if (!s.propertyValue) return 0;
  return Math.round((s.loanAmount / s.propertyValue) * 1000) / 10;
}

/** Stable cache/snapshot key. Contains no personal or provider data. */
export function scenarioKey(s: PublicRateScenario): string {
  return [
    "dscr",
    s.loanPurpose,
    s.state,
    s.zipCode || s.county || s.city || "-",
    Math.round(s.propertyValue),
    Math.round(s.loanAmount),
    s.fico,
    s.dscr.toFixed(2),
    s.propertyType,
    s.units,
    s.loanTerm,
    s.amortizationType,
    s.interestOnly ? "io" : "amort",
    s.lockPeriodDays,
    s.prepaymentPenalty,
    s.shortTermRental ? "str" : "ltr",
    s.entityVesting ? "entity" : "individual",
    s.citizenship,
    s.hasITIN ? "itin" : "no-itin",
  ].join(":");
}

// ---------------------------------------------------------------------------
// Public quote shape (the ONLY thing that may be rendered publicly)
// ---------------------------------------------------------------------------

export type PublicBadge =
  | "Balanced option"
  | "Lowest rate"
  | "Par pricing"
  | "Closest to par"
  | "Lender credit"
  | "Lowest APR"
  | "Lowest cost"
  | "Longest lock"
  | "Interest-only"
  | "No prepayment penalty";

/**
 * How complete the provider's fee data was for the displayed option. APR is
 * either the provider's own APR or our audited calculation when verified
 * borrower-paid finance charges sit outside price, so this tells the borrower
 * what that APR did and did not include.
 */
export type FeeDataStatus =
  /** The pricing engine returned itemized lender fees for this product. */
  | "provider_itemized"
  /** Origination-level fee data only. */
  | "provider_partial"
  /** No lender fee configuration came back. */
  | "not_provided";

export const FEE_STATUS_NOTE: Record<FeeDataStatus, string> = {
  provider_itemized:
    "Each APR reflects applicable borrower-paid finance charges and pricing credits for that exact quote, and may be the APR returned with the quote or our own audited calculation where verified borrower-paid finance charges outside the quoted price apply. The primary lender fee shown with each option comes from that lender's published fee schedule; other estimated lender and closing costs are listed separately and may not be included in the APR. Title, escrow and prepaid costs are not included.",
  provider_partial:
    "Each APR reflects applicable borrower-paid finance charges and pricing credits for that exact quote, and may be the APR returned with the quote or our own audited calculation where verified borrower-paid finance charges outside the quoted price apply. The primary lender fee shown with each option comes from that lender's published fee schedule; other estimated lender and closing costs are listed separately and may not be included in the APR.",
  not_provided:
    "Each APR reflects applicable borrower-paid finance charges and pricing credits for that exact quote, and may be the APR returned with the quote or our own audited calculation where verified borrower-paid finance charges outside the quoted price apply. The primary lender fee shown with each option comes from that lender's published fee schedule; other estimated lender and closing costs are listed separately and may not be included in the APR.",
};


/** How the quoted price translates into borrower cost or credit. */
export type PricingBasis = "par" | "points" | "credit" | "unknown";

/** One borrower-paid lender fee line, stripped of any investor identity. */
export type PublicFeeLine = { label: string; amount: number };

export type PublicRateOption = {
  /**
   * Opaque server-issued token. Carries no lender identity; it resolves
   * server-side to the internal investor/product metadata for this exact
   * quote when the borrower selects it and starts an application.
   */
  quoteToken?: string | null;
  /** Anonymous display label — never the investor's real name. */
  lenderLabel: string;
  /** Consumer-friendly program label, e.g. "30-Year Fixed DSCR". */
  programLabel: string;
  rate: number;
  /**
   * The APR that is displayed and sorted on. Either the pricing engine's own
   * APR or, where the quote's structure and finance charges support it, an
   * APR solved from this exact quote's cash flow (see `aprSource`).
   */
  apr: number;
  /**
   * Result of the APR provenance/sanity audit for this exact pricing row.
   * "review" means the value was still published verbatim but flagged
   * internally for staff verification.
   */
  aprAudit?: PublicAprAudit | null;
  /** Untouched pricing-engine APR for this row. Never overwritten. */
  obApr?: number | null;
  /** Whether `apr` was solved by us or passed through from the engine. */
  aprSource?: AprSource | null;
  aprBasis?: AprBasisCode | null;
  aprAuditStatus?: "ok" | "review" | null;
  /** Borrower-paid finance charges that fed the calculated APR. */
  financeChargesIncluded?: AprFinanceCharge[] | null;
  /** Charges deliberately kept OUT of APR, with the reason. */
  financeChargesExcluded?: AprExclusion[] | null;
  /** apr − obApr in percentage points. Null when the engine APR was kept. */
  divergenceFromObApr?: number | null;



  /**
   * Exact provider price (e.g. 100.349). Retained for staff traceability and
   * never shown as a headline number — borrowers see `pricingLabel` instead.
   */
  price: number | null;
  /** Plain-English pricing basis derived from the price and cost/credit. */
  pricingBasis: PricingBasis;
  /** Borrower-facing sentence for the pricing basis, e.g. "Par pricing — no points, no credit". */
  pricingLabel: string;
  /**
   * Result of the strict points / lender-credit reconciliation for this row.
   * When status is "fail" no points or credit figure is published — the row is
   * flagged internally for staff verification instead of guessing.
   */
  pricingAudit?: {
    status: "pass" | "fail";
    source: "provider" | "price_derived" | "none";
    failures: string[];
  } | null;

  /** Borrower-paid discount. Mutually exclusive with credit. */
  pointsPercent: number | null;
  pointsDollar: number | null;
  /** Lender credit / rebate. */
  creditPercent: number | null;
  creditDollar: number | null;
  /**
   * The single primary lender charge shown beside the quote (underwriting /
   * admin / lender fee), taken from our vetted schedule and labelled with the
   * lender's own fee name. Ancillary charges never appear here.
   */
  primaryLenderFee?: PrimaryLenderFee | null;
  /**
   * Itemized borrower-paid lender fees. Sourced from OUR internal lender fee
   * schedules (never the pricing engine's fee endpoint), resolved from the
   * investor that priced this quote.
   */
  lenderFeeLines: PublicFeeLine[];
  /**
   * Full evaluated fee-rule result for this quote: fixed lender fees,
   * conditional items, variable/third-party estimates and disclosures.
   */
  lenderFeeEstimate?: LenderFeeEstimate | null;
  /** Origination charge when the engine returned one separately. */
  originationCharge: number | null;
  /**
   * The primary lender fee amount, retained for audit/hand-off. It is NOT a
   * combined total and is never added into points, credit or cash to close.
   */
  totalUpfrontLenderCharges: number | null;
  /** Whether the quote was priced on the engine's fees-in basis. */
  feesIncludedInPrice: boolean | null;

  principalAndInterest: number | null;
  lockPeriod: number | null;
  badges: PublicBadge[];
  /**
   * Structural "what kind of quote is this" chips derived only from real
   * product/scenario data — e.g. "30-Year Fixed", "Interest Only", "DSCR",
   * "Foreign National", "ITIN", "5-Year PPP". Never inferred from silence.
   */
  attributeBadges: string[];
  /** Structural attributes surfaced so the card is self-explanatory. */
  interestOnly: boolean | null;
  /** What the displayed monthly figure actually represents. */
  paymentBasis: "principal_and_interest" | "interest_only" | null;
  amortizationLabel: string | null;
  prepaymentPenaltyLabel: string | null;
  feeStatus: FeeDataStatus;
  /**
   * Fee-schedule match quality. Only set on quotes that come from the central
   * engine contract; `no_verified_match` / `ambiguous` mean fees are UNKNOWN,
   * never zero.
   */
  feeMappingStatus?: "matched" | "ambiguous" | "no_verified_match" | null;
  /**
   * Plain-English, deterministic reasons this option was selected. Rendered
   * to the borrower and stored with staff quotes for traceability.
   */
  selectionReasons: string[];
};

/**
 * Upgrades a stored (possibly older) public option to the current shape.
 *
 * Snapshots written before the consumer-terminology change carry raw price
 * badges and no pricing basis. Rather than showing borrowers "Price 100" or an
 * empty cost column, we re-derive the plain-English representation from the
 * fields the snapshot already holds. Nothing is invented: when the snapshot has
 * no points, credit or price we fall back to the "confirmed in writing" copy.
 */
export function hydratePublicOption(raw: PublicRateOption): PublicRateOption {
  const legacyBadge = (b: string): PublicBadge =>
    b === "At 100 price" ? "Par pricing" : b === "Closest to 100 price" ? "Closest to par" : (b as PublicBadge);

  const points = raw.pointsDollar ?? null;
  const pointsPct = raw.pointsPercent ?? null;
  const credit = raw.creditDollar ?? null;
  const creditPct = raw.creditPercent ?? null;

  const basis: PricingBasis =
    raw.pricingBasis ??
    (credit != null && credit > 0
      ? "credit"
      : points != null && points > 0
        ? "points"
        : raw.price != null && Math.abs(raw.price - 100) < 0.0005
          ? "par"
          : "unknown");

  const money = (n: number) => `$${Math.round(Math.abs(n)).toLocaleString("en-US")}`;
  const label =
    raw.pricingLabel ??
    (basis === "points"
      ? pointsPct != null && points != null
        ? `${pointsPct.toFixed(3)} points (${money(points)})`
        : points != null
          ? `${money(points)} in discount points`
          : "Discount points apply at closing"
      : basis === "credit"
        ? credit != null && creditPct != null
          ? `Lender credit: ${money(credit)} (${creditPct.toFixed(3)}%)`
          : credit != null
            ? `Lender credit: ${money(credit)}`
            : "Lender credit toward closing costs"
        : basis === "par"
          ? "Par — 0 points"
          : "Upfront pricing confirmed on your written quote");

  const feeLines = raw.lenderFeeLines ?? [];

  // Older snapshots predate the primary-fee architecture; re-derive it from the
  // stored fee estimate rather than showing a combined lender-fee total.
  const feesInBasis =
    raw.primaryLenderFee?.feesInBasis ??
    (raw.feesIncludedInPrice == null ? null : raw.feesIncludedInPrice ? "Yes" : "No");
  const primary =
    raw.primaryLenderFee ?? resolvePrimaryLenderFee(raw.lenderFeeEstimate ?? null, feesInBasis);

  return {
    ...raw,
    badges: (raw.badges ?? []).map(legacyBadge),
    attributeBadges: raw.attributeBadges ?? [],
    pricingBasis: basis,
    pricingLabel: label,
    lenderFeeLines: feeLines,
    primaryLenderFee: primary,
    originationCharge: raw.originationCharge ?? null,
    totalUpfrontLenderCharges: primary?.amount ?? null,
    feesIncludedInPrice: raw.feesIncludedInPrice ?? null,
    lenderFeeEstimate: raw.lenderFeeEstimate ?? null,
    aprAudit: raw.aprAudit ?? null,
  };


}

export type ProviderParQuote = { parRate: number | null; parPrice: number | null; parLock: number | null };

export type PublicRateResponse = {
  ok: boolean;
  /** "live" from a fresh provider call, "cache" from a recent identical call,
   *  "snapshot" from the stored public snapshot, "unavailable" when nothing. */
  source: "live" | "cache" | "snapshot" | "unavailable";
  asOf: string | null;
  /** When published pricing last actually changed (stored snapshot only). */
  ratesChangedAt?: string | null;
  scenario: PublicRateScenario;
  ltv: number;
  options: PublicRateOption[];
  /** Provider par quotes, clearly separate from an actual price=100 quote. */
  providerParQuotes: ProviderParQuote[];
  /**
   * Lock periods the pricing engine actually returned for this scenario. The
   * client selector offers only these; the displayed table is always a single
   * lock period, never a mix.
   */
  availableLockPeriods: number[];
  /** Consumer-safe explanation when no options can be shown. */
  message: string | null;
  /** Age classification for everything above. Never fabricated. */
  freshness: Freshness;
  /** Where we can originate this loan. */
  availability: StateAvailability;
  /** Worst fee-data status across the displayed options. */
  feeDataStatus: FeeDataStatus;
  /** Opaque per-request id for support + log correlation. Contains no PII. */
  correlationId: string | null;
  /** Additional consumer-safe notices (stale market, entity vesting, etc.). */
  notices: string[];
};

export function emptyRateResponse(
  scenario: PublicRateScenario,
  message: string | null,
  extra: Partial<PublicRateResponse> = {},
): PublicRateResponse {
  return {
    ok: false,
    source: "unavailable",
    asOf: null,
    scenario,
    ltv: scenarioLtv(scenario),
    options: [],
    providerParQuotes: [],
    availableLockPeriods: [],
    message,
    freshness: {
      status: "expired",
      asOf: null,
      ageMinutes: null,
      label: "No pricing timestamp is available for this scenario.",
      displayable: false,
      requiresStaleNotice: false,
    },
    availability: {
      status: "available",
      stateAbbrev: scenario.state,
      stateName: null,
      stateSlug: null,
      consumerLicensed: false,
      canShowPricing: true,
      message: null,
    },
    feeDataStatus: "not_provided",
    correlationId: null,
    notices: [],
    ...extra,
  };
}

/** Worst-case fee status across a set of options (used for the page-level note). */
export function aggregateFeeStatus(options: PublicRateOption[]): FeeDataStatus {
  if (!options.length) return "not_provided";
  if (options.some((o) => o.feeStatus === "not_provided")) return "not_provided";
  if (options.some((o) => o.feeStatus === "provider_partial")) return "provider_partial";
  return "provider_itemized";
}

// ---------------------------------------------------------------------------
// Program scenarios kept warm by the scheduled pricing refresh. Each one is a
// single representative scenario (not all-market pricing); public blocks label
// them as such. Variants change exactly one input from the standard scenario.
// ---------------------------------------------------------------------------
export type PricingProgram = "standard" | "foreignNational" | "fourUnit" | "interestOnly" | "lowDscr" | "shortTermRental";

export const PROGRAM_SCENARIOS: Record<PricingProgram, PublicRateScenario> = {
  standard: { ...DEFAULT_RATE_SCENARIO },
  // FN purchase at 70% LTV (program max 75%), otherwise identical.
  foreignNational: { ...DEFAULT_RATE_SCENARIO, loanAmount: 350_000, citizenship: "ForeignNational" },
  fourUnit: { ...DEFAULT_RATE_SCENARIO, units: 4 },
  interestOnly: { ...DEFAULT_RATE_SCENARIO, interestOnly: true },
  // No-ratio tier: DSCR 0.90 at 75% LTV / 740 FICO / $375K (inside 0.75–0.99 guideline).
  lowDscr: { ...DEFAULT_RATE_SCENARIO, dscr: 0.9 },
  shortTermRental: { ...DEFAULT_RATE_SCENARIO, shortTermRental: true },
};

export const PROGRAM_SCENARIO_LABEL: Record<PricingProgram, string> = {
  standard: "30-year fixed DSCR purchase, single-family, 75% LTV, 740 FICO, 1.20 DSCR",
  foreignNational: "Foreign National 30-year fixed DSCR purchase, single-family, 70% LTV, 1.20 DSCR",
  fourUnit: "30-year fixed DSCR purchase, 4-unit, 75% LTV, 740 FICO, 1.20 DSCR",
  interestOnly: "Interest-only 30-year DSCR purchase, single-family, 75% LTV, 740 FICO, 1.20 DSCR",
  lowDscr: "30-year fixed DSCR purchase, single-family, 75% LTV, 740 FICO, 0.90 DSCR",
  shortTermRental: "Short-term rental 30-year fixed DSCR purchase, single-family, 75% LTV, 740 FICO, 1.20 DSCR",
};

export function programScenarioKey(p: PricingProgram): string {
  return scenarioKey(PROGRAM_SCENARIOS[p]);
}

/** Snapshots priced in the same scheduled run land within this window. */
export const SAME_RUN_WINDOW_MS = 3 * 60 * 60 * 1000;

/**
 * Lowest-rate difference between two snapshots, only when both are usable and
 * were priced in the same/compatible run. Otherwise null (never a typed spread).
 */
export function comparableSpread(
  a: { min: number | null; asOfMs: number | null },
  b: { min: number | null; asOfMs: number | null },
): number | null {
  if (a.min == null || b.min == null || a.asOfMs == null || b.asOfMs == null) return null;
  if (Math.abs(a.asOfMs - b.asOfMs) > SAME_RUN_WINDOW_MS) return null;
  return Math.round((a.min - b.min) * 1000) / 1000;
}
